import {Formik} from "formik";
import {useCallback, useEffect, useRef, useState} from "react";
import * as Yup from "yup";
import {useAddOffer} from "../../../hooks/useOffers";
import {getOfferPriceEstimate} from "../../../services/EstimationService";
import UpImage from "../../../images/image-upload.svg";
import NoImage from "../../../images/NoImage.jpg";
import videoImage from "../../../images/thumbVideo.png";
import ImageComponent from "../../components/shared/ImageComponent";
import {geocodeByPlaceId} from "react-google-places-autocomplete";
import GooglePlacesAutocomplete from "../../components/shared/SafeGooglePlacesAutocomplete";
import "./customEditor.css";
import {useMe} from "../../../hooks/useGetUser";
import Loader from "../../components/shared/Loader";
import {
  emailRegex,
  phoneRegex,
} from "../../../utils/constants";
import {showToastModal} from "../../components/shared/ToastModalSwal";
import {
  MAX_OFFER_ATTACHMENTS,
  mergeOfferAttachments,
} from "../../../utils/offerAttachments";
import QueryErrorState from "../../components/shared/QueryErrorState";
import UploadPreviewState from "../../components/shared/UploadPreviewState";
import googleMapsApiKey from "../../../config/googleMaps";

const FormAjoutAnnonce = ({setGoSteps, selectedSubCategory}) => {
  const [images, setImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const {data: user, isError, isLoading, error, refetch} = useMe();
  const {address = "", zipCode = "", city = ""} = user ?? {};

  const inputRef = useRef();
  const {onAddOfferAsync, isAdding} = useAddOffer();
  const [estimation, setEstimation] = useState(null);
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState("");
  const [titleDescription, setTitleDescription] = useState({
    title: "",
    description: "",
    postCode: "",
  });
  const estimateTimeout = useRef(null);
  const estimateRequest = useRef({id: 0, controller: null});

  const fetchPriceEstimate = useCallback(async ({
    title,
    description,
    postCode,
  }) => {
    const text = [title, description].filter(Boolean).join(" ").trim();
    const subCategoryIds = selectedSubCategory.map((item) => item.id);
    if (text.length < 20 || !subCategoryIds.length) {
      setEstimation(null);
      setEstimateError("");
      return;
    }

    estimateRequest.current.controller?.abort();
    const controller = new AbortController();
    const requestId = estimateRequest.current.id + 1;
    estimateRequest.current = {id: requestId, controller};
    setIsEstimating(true);
    setEstimateError("");

    try {
      const result = await getOfferPriceEstimate({
        title,
        description,
        postCode,
        subCategoryIds,
        signal: controller.signal,
      });
      if (estimateRequest.current.id !== requestId) return;
      setEstimation(result);
    } catch (error) {
      if (error?.code === "ERR_CANCELED" || controller.signal.aborted) return;
      if (estimateRequest.current.id !== requestId) return;
      setEstimation(null);
      setEstimateError(
        error?.response?.data?.error
          || "Impossible de calculer l'estimation pour le moment.",
      );
    } finally {
      if (estimateRequest.current.id === requestId) {
        setIsEstimating(false);
      }
    }
  }, [selectedSubCategory]);

  useEffect(() => {
    if (estimateTimeout.current) {
      window.clearTimeout(estimateTimeout.current);
    }

    const title = titleDescription.title.trim();
    const description = titleDescription.description.trim();
    const postCode = titleDescription.postCode.trim();

    if (!title && !description) {
      setEstimation(null);
      setEstimateError("");
      return;
    }

    estimateTimeout.current = window.setTimeout(() => {
      fetchPriceEstimate({title, description, postCode});
    }, 1200);

    return () => {
      if (estimateTimeout.current) {
        window.clearTimeout(estimateTimeout.current);
      }
      estimateRequest.current.controller?.abort();
    };
  }, [titleDescription, fetchPriceEstimate]);

  const handleFormChange = (e, formChange) => {
    formChange(e);
    if (e.target.name === "title" || e.target.name === "description") {
      setTitleDescription((prev) => ({
        ...prev,
        [e.target.name]: e.target.value,
      }));
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const addAttachments = (fileList) => {
    const {attachments, errors} = mergeOfferAttachments({
      currentAttachments: images,
      incomingFiles: fileList,
    });
    setImages(attachments);

    if (errors.length) {
      showToastModal({
        success: false,
        title: "Erreur d'importation",
        message: errors.join(" "),
        buttonTitle: "Fermer",
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addAttachments(e.dataTransfer.files);
      e.dataTransfer.clearData();
    }
  };

  const handleDiplome = (e) => {
    const input = e.target;
    addAttachments(input.files);
    input.value = "";
  };

  const handleDelete = (index) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index]?.imageUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handlePlaceSelect = async (address, setFieldValue, setFieldTouched) => {
    const [place] = await geocodeByPlaceId(address.value.place_id);
    const {long_name: postalCode = ""} =
      place.address_components.find((c) => c.types.includes("postal_code")) ||
      {};
    const {long_name: ville = ""} =
      place.address_components.find((c) => c.types.includes("locality")) || {};
    const street =
      place.address_components.find((c) => c.types.includes("route"))
        ?.long_name || "";
    const streetNumber =
      place.address_components.find((c) => c.types.includes("street_number"))
        ?.long_name || "";

    setFieldValue("streetNumber", streetNumber);
    setFieldValue("street", street);

    setFieldValue("address", address.label);
    setFieldValue("postCode", postalCode);
    setFieldValue("ville", ville);
    setFieldTouched("address", true);
    setFieldTouched("postCode", true);
    setFieldTouched("ville", true);
    setSelectedAddress(address);
    setTitleDescription((prev) => ({
      ...prev,
      postCode: postalCode || "",
    }));
  };
  const handleSubmit = async (values, {setSubmitting}) => {
    try {
      if (!selectedSubCategory.length) {
        showToastModal({
          title: "Erreur",
          message: "Veuillez sélectionner une sous-catégorie",
        });
        return;
      }

      const offerValues = {...values};
      delete offerValues.street;
      delete offerValues.streetNumber;
      await onAddOfferAsync({
        body: {
          ...offerValues,
          subCategory: selectedSubCategory.map((el) => el.id),
          files: images,
        },
        onUploadProgress: ({loaded, total}) =>
          setUploadProgress(total ? Math.round((loaded / total) * 100) : 0),
      });
    } catch (error) {
      // Error handling: form submission failed
    } finally {
      setSubmitting(false);
    }
  };

  const formSchema = Yup.object().shape({
    title: Yup.string()
      .required("Le titre est requis")
      .min(2, "Le titre doit contenir au moins 2 caractères")
      .test(
        "no-phone-or-email",
        "Le titre ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
        (value) => {
          if (!value) return true;
          return !emailRegex.test(value) && !phoneRegex.test(value);
        },
      ),
    description: Yup.string()
      .required("La description est requise")
      .min(2, "La description doit contenir au moins 2 caractères")
      .test(
        "no-phone-or-email",
        "La description ne doit pas contenir de numéro de téléphone ou d'adresse e-mail",
        (value) => {
          if (!value) return true;
          return !emailRegex.test(value) && !phoneRegex.test(value);
        },
      ),
    address: Yup.string(),

    ville: Yup.string()
      .required("veuillez entrer votre adresse complete vill ")
      .min(2, "La ville doit contenir au moins 2 caractères"),
    postCode: Yup.string()
      .required("veuillez entrer votre adresse complete pos")
      .matches(/^\d{5}$/, "Le code postal doit contenir exactement 5 chiffres"),
    street: Yup.string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema.required(
            "Veuillez entrer votre adresse complète contenant le nom de rue",
          ),

        otherwise: (schema) => schema.notRequired(),
      }),
    streetNumber: Yup.string()
      .nullable()
      .when("address", {
        is: (val) => val && val.length > 0, // Check if address is not empty
        then: (schema) =>
          schema.required(
            "Veuillez entrer votre adresse complète contenant le numéro de la rue",
          ),

        otherwise: (schema) => schema.notRequired(),
      }),

    minBudget: Yup.number()
      .required("Le budget minimum est requis")
      .positive("Le budget minimum doit être un nombre positif")
      .min(1, "Le budget minimum doit être supérieur ou égal à 1 euro"),

    maxBudget: Yup.number()
      .required("Le budget maximum est requis")
      .positive("Le budget maximum doit être un nombre positif")
      .moreThan(
        Yup.ref("minBudget"),
        "Le budget maximum doit être supérieur au budget minimum",
      ),
  });
  if (isLoading) return <Loader />;
  if (isError) return <QueryErrorState error={error} onRetry={refetch} />;

  return (
    <section>
      <Formik
        initialValues={{
          title: "",
          description: "",
          address: address || "",
          ville: city || "",
          postCode: zipCode || "",
          street: address,
          streetNumber: address,
          minBudget: "",
          maxBudget: "",
        }}
        validateOnChange={true}
        validateOnBlur={true}
        validationSchema={formSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          submitCount,

          touched,
        }) => (
          <form
            onSubmit={handleSubmit}
            className="mx-auto !px-2 sm:!px-3 md:!px-5 lg:!px-10 xl:!px-14 2xl:!px-20 !mt-7 "
          >
            <h3 className="text-left font-semibold leading-3 text-[18px]">
              Remplissez les détails
            </h3>
            <div className="mb-4 rounded-xl border border-[#d7dce9] bg-[#f8fafc] p-4 text-left">
              <div className="text-sm font-semibold text-[#1F2937] mb-2">
                Estimation de prix
              </div>
              {isEstimating ? (
                <div className="text-sm text-[#1A64B9]">Calcul en cours...</div>
              ) : estimation ? (
                <>
                  <div className="text-sm text-[#1A64B9]">
                    Prix estimé : {estimation.min} € – {estimation.max} € TTC
                    {estimation.service ? ` pour ${estimation.service}` : ""}
                  </div>
                  <div className="mt-1 text-xs text-[#6b7280]">
                    {estimation.webUsed
                      ? "Recherche web Mistral"
                      : "Données Help Clic"}
                    {estimation.historicalCount > 0
                      ? ` + ${estimation.historicalCount} annonce${estimation.historicalCount > 1 ? "s" : ""} Help Clic`
                      : ""}
                    {estimation.localHistoricalSample ? " de votre département" : ""}.
                  </div>
                </>
              ) : estimateError ? (
                <div className="text-sm text-[#dc2626]">{estimateError}</div>
              ) : (
                <div className="text-sm text-[#6b7280]">
                  Commencez à écrire le titre ou la description pour obtenir une
                  estimation.
                </div>
              )}
            </div>
            <div className="w-full !px-2 py-2 flex flex-col gap-4 justify-center mt-4">
              <div className="grid grid-cols-2 w-full gap-4">
                <FormInput
                  className={"w-full col-span-2 "}
                  label="Titre de l'annonce"
                  name="title"
                  required
                  type="text"
                  value={values.title}
                  handleChange={(e) => handleFormChange(e, handleChange)}
                  handleBlur={handleBlur}
                  error={errors.title}
                  touched={touched.title}
                />

                <FormTextArea
                  className={"w-full col-span-2   "}
                  label="Description"
                  name="description"
                  required
                  value={values.description}
                  handleChange={(e) => handleFormChange(e, handleChange)}
                  handleBlur={handleBlur}
                  error={errors.description}
                  touched={touched.description}
                />
                <div className="form-group w-full col-span-2">
                  <label className="required-field-label text-base font-medium leading-3 text-[#7E7E7E]">
                    Adresse
                  </label>
                  <GooglePlacesAutocomplete
                    apiKey={googleMapsApiKey}
                    autocompletionRequest={{
                      componentRestrictions: {country: ["fr"]},
                      types: ["address"],
                    }}
                    selectProps={{
                      value: selectedAddress,
                      defaultInputValue: values.address,
                      onChange: (address) =>
                        handlePlaceSelect(
                          address,
                          setFieldValue,
                          setFieldTouched,
                        ),
                      placeholder: "Recherchez une adresse...",
                      styles: {
                        dropdownIndicator: (base) => ({
                          ...base,
                          display: "none", // Hides the dropdown icon
                        }),
                        indicatorSeparator: (base) => ({
                          ...base,
                          display: "none", // Hides the separator line
                        }),
                        control: (base) => ({
                          ...base,
                          borderColor: "#eee", // Matches the other inputs' border color
                          borderWidth: "1px",
                          borderRadius: "0.375rem", // Rounded corners like your other inputs
                          padding: "0.375rem 0.75rem", // Add padding to match the input
                          boxShadow: "none",
                        }),
                        input: (base) => ({
                          ...base,
                          fontSize: "0.875rem", // Matches your other inputs' font size
                          color: "#374151", // Text color
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: "#9ca3af", // Placeholder color
                          fontSize: "0.875rem",
                        }),
                      },
                    }}
                  />
                  <div className="text-left text-xs mt-1 ">
                    Votre adresse ne sera pas visible par les prestataires.
                  </div>
                  {submitCount > 0 &&
                    (touched.address || touched.postCode || touched.ville) &&
                    (errors.address || errors.postCode || errors.ville) && (
                      <div
                        className="invalid-feedback animated fadeInUp"
                        style={{display: "block"}}
                      >
                        {errors.address || errors.postCode || errors.ville}
                      </div>
                    )}
                </div>
                <FormInput
                  className={"col-span-2 md:col-span-1"}
                  label="Budget minimum"
                  name="minBudget"
                  placeholder={"Minimum(€)"}
                  type="number"
                  min={1}
                  required
                  value={values.minBudget}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={errors.minBudget}
                  touched={touched.minBudget}
                />
                <FormInput
                  className={"col-span-2 md:col-span-1"}
                  label="Budget maximum"
                  placeholder={"Maximum(€)"}
                  name="maxBudget"
                  type="number"
                  min={1}
                  required
                  value={values.maxBudget}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  error={errors.maxBudget}
                  touched={touched.maxBudget}
                />
              </div>

              <ImageUploadSection
                handleDragEnter={handleDragEnter}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                images={images}
                handleDiplome={handleDiplome}
                handleDelete={handleDelete}
                inputRef={inputRef}
                uploadProgress={uploadProgress}
                uploading={isSubmitting || isAdding}
              />

              <div className="text-center !mt-8 gap-2 w-full flex flex-col md:flex-row justify-end">
                <button
                  className="btn !min-w-[250px] !rounded-lg bg-white !text-[#0D44B1] !border-[#0D44B1] !border border-solid"
                  onClick={() => setGoSteps(1)}
                >
                  Retour
                </button>
                <button
                  data-testid="offer-create-submit"
                  className="btn !min-w-[250px] !rounded-lg bg-gradient-to-r from-[#0022AA] to-[#1A64B9] text-white sw-btn-next"
                  disabled={isSubmitting || isAdding}
                  type="submit"
                >
                  {isSubmitting || isAdding ? "Publication..." : "Publier"}
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </section>
  );
};

const FormInput = ({
  label,
  name,
  type,
  value,
  placeholder,
  handleChange,
  handleBlur,
  className,
  error,
  touched,
  min,
  required = false,
}) => (
  <div className={"form-group flex-grow " + className}>
    <label className="text-base font-medium leading-3 text-[#7E7E7E]">
      {label}
    </label>
    <input
      type={type}
      className="form-control"
      name={name}
      min={min}
      aria-required={required}
      placeholder={placeholder}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
    />
    {error && touched && (
      <div
        className="invalid-feedback animated fadeInUp"
        style={{display: "block"}}
      >
        {error}
      </div>
    )}
  </div>
);

const FormTextArea = ({
  label,
  name,
  value,
  handleChange,
  handleBlur,
  className,
  error,
  touched,
  required = false,
}) => (
  <div className={"form-group  flex flex-col pt-1 " + className}>
    <label className="text-base font-medium leading-3 text-[#7E7E7E]">
      {label}
    </label>
    <textarea
      className="form-control flex-grow"
      name={name}
      aria-required={required}
      onChange={handleChange}
      onBlur={handleBlur}
      value={value}
      rows={5}
    />
    {error && touched && (
      <div
        className="invalid-feedback animated fadeInUp"
        style={{display: "block"}}
      >
        {error}
      </div>
    )}
  </div>
);

const ImageUploadSection = ({
  images,
  handleDiplome,
  handleDelete,
  handleDragOver,
  handleDragEnter,
  handleDragLeave,
  handleDrop,
  inputRef,
  uploadProgress,
  uploading,
}) => (
  <div className="flex flex-col w-full justify-start  gap-2">
    <div className="text-base font-medium leading-3 text-[#7E7E7E]">
      Pièces jointes
    </div>
    <div className="grid grid-cols-2 w-full gap-2">
      <div className="col-span-2 lg:col-span-1">
        <div
          onClick={() => inputRef.current.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className="flex flex-col px-3 py-3 justify-center items-center w-full  border-2 border-[#1A64B9] border-dashed rounded-xl cursor-pointer"
        >
          <img src={UpImage} alt="upload" className="w-auto h-[50px] mb-2" />
          <h3 className="font-semibold text-center w-[300px] text-sm text-[#19295C]">
            Faites glisser ou importez vos photos/vidéos
          </h3>
          <div className="font-semibold text-center w-full text-[12px] text-[#7e7e7e]">
            JPG, PNG, MP4 · {MAX_OFFER_ATTACHMENTS} fichiers à la fois · 10 Mo max par fichier
          </div>
        </div>
        <input
          type="file"
          ref={inputRef}
          className="hidden"
          onChange={handleDiplome}
          accept="image/*,video/*"
          multiple
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 w-full col-span-2 lg:col-span-1 gap-3">
        {!!images.length && (
          <div className="col-span-full text-xs font-medium text-[#1A64B9]">
            {images.length} pièce{images.length > 1 ? "s" : ""} jointe
            {images.length > 1 ? "s" : ""} sélectionnée
            {images.length > 1 ? "s" : ""}
          </div>
        )}
        {images.map((image, index) => (
          <div key={`${image.file?.name}-${image.file?.lastModified}-${index}`}>
            <UploadPreviewState
              uploading={uploading}
              progress={uploadProgress}
              className="aspect-square"
            >
              {image.file?.type.includes("video") ? (
                <VideoPreview
                  url={image?.imageUrl}
                  handleDelete={() => handleDelete(index)}
                />
              ) : (
                <ImagePreview
                  handleDelete={() => handleDelete(index)}
                  imageUrl={image?.imageUrl}
                />
              )}
            </UploadPreviewState>
            <div className="mt-1 truncate text-xs text-[#7e7e7e]">
              {image.file?.name}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);
const ImagePreview = ({imageUrl, handleDelete}) => (
  <div className="w-full  aspect-square bg-white flex flex-col justify-center items-center relative rounded-xl">
    <div
      onClick={handleDelete}
      className="w-6 h-6 rounded-full flex justify-center items-center bg-[#FF0000] absolute z-10 top-[10px] right-[10px] text-[16px] cursor-pointer text-white"
    >
      <i className="fi fi-rr-trash flex justify-center items-center"></i>
    </div>
    <ImageComponent
      src={imageUrl}
      alt="Selected"
      className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
      fallback={
        <img
          src={NoImage}
          alt="Selected"
          className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
        />
      }
    />
  </div>
);

const VideoPreview = ({url, handleDelete}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const handlePlayVideo = () => {
    setIsPlaying(true);
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  return (
    <div className="w-full  aspect-square bg-white flex flex-col justify-center items-center relative rounded-xl">
      <div
        onClick={handleDelete}
        className="w-6 h-6 rounded-full flex justify-center items-center bg-[#FF0000] absolute z-10 top-[10px] right-[10px] text-[16px] cursor-pointer text-white"
      >
        <i className="fi fi-rr-trash flex justify-center items-center"></i>
      </div>
      {!isPlaying ? (
        <img
          className="w-full aspect-square cursor-pointer object-cover border-2 border-solid rounded-xl"
          src={videoImage}
          alt="thumb"
          onClick={handlePlayVideo}
        />
      ) : (
        <video
          src={url}
          alt="Selected"
          className="w-full aspect-square object-cover border-2 border-solid  rounded-xl "
          type="video/mp4"
          ref={videoRef}
          autoPlay
          controls
        />
      )}
    </div>
  );
};

export default FormAjoutAnnonce;
